var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { Fragment, useCallback } from "react";
import MobilePage, { PageHeader } from "../../../Components/MobilePage";
import { Trans, useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatCurrency, formatFullDate } from "../../../../utils";
import AppBar from "../../Components/AppBar";
import { OrderHistoryLine, } from "./components/OrderHistoryLine";
import { OrderList } from "@/react/Webshop/Components/MyAccount/OrderList";
import OrderTermsConsent from "@/react/Webshop/Components/MyAccount/OrderTermsConsent";
import { useAPI } from "@/react/lib/common";
import { isServerProductOrderLine, ORDER_LINE_TYPE, } from "@/react/Webshop/types/ShoppingCart";
import { PRODUCT_INVOICE_UUID } from "@/react/Webshop/providers/PriceListProvider";
import { Accordion, AccordionSummary, AccordionDetails, Container, Box, styled, useTheme, Skeleton, Divider, Typography, } from "@mui/material";
import { useHistory, useParams } from "react-router";
import useMobileRoutes from "../../hooks/useMobileRoutes";
import { useShoppingCart } from "@/react/Webshop/providers/ShoppingCartProvider";
import { toast } from "react-toastify";
import useGoogleFonts from "@/react/hooks/useGoogleFonts";
import { templateFontList } from "@/react/components/Template/common";
const Column = styled("div")({
    flexBasis: "33.33%",
});
const MyOrders = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const routes = useMobileRoutes();
    const { addItem } = useShoppingCart();
    const [orderList, loading] = useAPI("/ajax/user/get_order_list.php");
    const onBuyAgain = useCallback((orderLine) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const data = yield addItem(Object.assign(Object.assign({}, orderLine), { quantity: 1, isUpdating: false, isDeleting: false }));
            if (data.error) {
                toast.error(data.error);
                return;
            }
            history.push(routes.shoppingcart.path);
        }
        catch (_a) {
            toast.error(t("Could not add the product to the shopping cart."));
        }
    }), [addItem, history, routes.shoppingcart.path, t]);
    if (loading) {
        return null;
    }
    return (React.createElement(MobilePage, { requireUser: true },
        React.createElement(AppBar, null),
        React.createElement(PageHeader, null,
            React.createElement(Trans, null, "My orders")),
        React.createElement(Container, { sx: { paddingBottom: 6 } },
            React.createElement(OrderList, null, orderList === null || orderList === void 0 ? void 0 : orderList.map((order) => {
                return (React.createElement(Order, { key: order.uuid, order: order, onBuyAgain: onBuyAgain }));
            })))));
};
const Order = ({ order, onBuyAgain, }) => {
    const theme = useTheme();
    const history = useHistory();
    const routes = useMobileRoutes();
    const { orderUuid } = useParams();
    const isExpanded = orderUuid === order.uuid;
    const handleChange = (_event, expanded) => {
        history.replace(expanded
            ? `${routes["myaccount.orders"].path}/${order.uuid}`
            : routes["myaccount.orders"].path);
    };
    return (React.createElement("li", null,
        React.createElement(Accordion, { expanded: isExpanded, onChange: handleChange },
            React.createElement(AccordionSummary, { expandIcon: React.createElement(ExpandMoreIcon, null), style: {
                    backgroundColor: isExpanded
                        ? theme.palette.secondary.main
                        : theme.palette.primary.light,
                    color: isExpanded
                        ? theme.palette.secondary.contrastText
                        : theme.palette.primary.dark,
                } },
                React.createElement(Column, null, formatFullDate(order.confirmTime)),
                React.createElement(Column, null,
                    "#",
                    order.uuid.substring(0, 8)),
                React.createElement(Column, null, formatCurrency(order.totalPrice))),
            React.createElement(AccordionDetails, null, isExpanded && (React.createElement(OrderDetails, { uuid: order.uuid, onBuyAgain: onBuyAgain }))))));
};
export const OrderDetails = ({ uuid, onBuyAgain, }) => {
    const [details, isLoading, error] = useAPI("/sections/my-account/ajax/get_order_details.php?uuid=" + uuid);
    // The order line previews draw templates with these fonts:
    useGoogleFonts(templateFontList);
    if (isLoading) {
        return React.createElement(Skeleton, { animation: "pulse", variant: "text" });
    }
    if (error || !details) {
        return React.createElement(Trans, null, "Could not load the order details");
    }
    const productLines = details.orderLines
        .filter(isServerProductOrderLine)
        .filter((ol) => ol.product.uuid !== PRODUCT_INVOICE_UUID);
    const summaryLines = details.orderLines.filter((ol) => {
        var _a;
        return ol.type !== ORDER_LINE_TYPE.PRODUCT ||
            ((_a = ol.product) === null || _a === void 0 ? void 0 : _a.uuid) === PRODUCT_INVOICE_UUID;
    });
    return (React.createElement(Box, { display: "flex", flexDirection: "column", flex: 1 },
        productLines.map((orderLine) => (React.createElement(Fragment, { key: orderLine.uuid },
            React.createElement(OrderHistoryLine, { orderLine: orderLine, onBuyAgain: onBuyAgain }),
            React.createElement(Divider, null)))),
        React.createElement(Box, { paddingTop: 1.5 }, summaryLines.map((orderLine, index) => {
            const isTotal = orderLine.type === ORDER_LINE_TYPE.TOTAL;
            return (React.createElement(Box, { key: index, display: "flex", justifyContent: "space-between" },
                React.createElement(Typography, { variant: isTotal ? "body1" : "body2", fontWeight: isTotal ? "bold" : undefined }, orderLine.description),
                React.createElement(Typography, { variant: isTotal ? "body1" : "body2", fontWeight: isTotal ? "bold" : undefined }, formatCurrency(orderLine.linePrice))));
        })),
        details.termsConsent && (React.createElement(OrderTermsConsent, { termsConsent: details.termsConsent }))));
};
export default MyOrders;
